<template>
  <VButtonAnimated
    class="text-xl font-semibold w-full h-full"
    :disabled="isLoading || !currentProduct.enabled || !hasQuantity"
    @click="addProductToCart"
  >
    <span class="gap-3 flex">
      <ShoppingBagIcon class="w-6" />
      {{ trans.get('miliboo.product.buy') }}
    </span>
  </VButtonAnimated>
  <product-confirmation-modal />
</template>
<script setup>
import {
  ref, provide, onMounted, markRaw, inject,
  computed,
} from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { toast } from 'vue-sonner';
import { ShoppingBagIcon } from '@heroicons/vue/24/outline';

import ProductConfirmationModal from './ProductConfirmationModal.vue';
import useProduct from '../../uses/useProduct';
import VNotificationBanner from '../notifications/VNotificationBanner.vue';
import useAddToCart from '../../features/cart/composable/useAddToCart';
import VButtonAnimated from '../buttons/VButtonAnimated.vue';

const queryClient = useQueryClient();
const {
  setProduct, product, quantity,
} = useProduct();
const { addToCart, isLoading, addToCartTag } = useAddToCart();
const trans = inject('translate');
const props = defineProps({
  currentProduct: {
    type: Object,
    required: true,
  },
  prices: {
    type: Object,
    required: true,
  },
});

const modal = ref({
  open: false,
  payload: null,
});

const triggerNotification = (msg) => {
  toast.custom(markRaw(VNotificationBanner), {
    componentProps: {
      message: msg,
      level: 'warning',
      duration: 4,
    },
    duration: 4 * 1000,
  });
};

const addProductToCart = function () {
  addToCart(product.value.id, quantity.value, {
    onSuccess: () => {
      addToCartTag(product.value, quantity.value, props.prices);
      modal.value.open = true;
      queryClient.invalidateQueries({ queryKey: ['cart'], exact: true });
      queryClient.invalidateQueries({ queryKey: ['user'], exact: true });
    },
    onError: (error) => {
      if (error.response.data.message === 'quantity not available') {
        triggerNotification(trans.get('miliboo.product.not_enough_stock'));
      }
    },
  });
};

onMounted(() => {
  setProduct(props.currentProduct);
});

provide('modal', modal);

const hasQuantity = computed(() => {
  const [availabilities] = props.currentProduct.availabilities;
  return (availabilities?.quantity ?? 0) > 0;
});

</script>
